/**
 * This utility traverses a nested object structure and replaces specific property values
 * with their "plus" version counterparts. Specifically:
 *
 * - Replaces 'ai_description' content with 'ai_plus_description' content when both exist at the same level
 * - Replaces 'key_insights' content with 'key_insights_plus' content when both exist at the same level
 * - Replaces 'conclusion' content with 'conclusion_plus' content when both exist at the same level
 *
 * This is a recursive function that handles nested arrays and objects.
 *
 * @param obj - The object to process
 * @returns The processed object with "plus" content copied to standard properties
 */
function washReportDocument(obj: any): any {
  // If not an object or array, return as is
  if (obj === null || typeof obj !== 'object') {
    return obj
  }

  // Define the pairs of properties to look for (plus version and standard version)
  const propertyPairs = [
    { plus: 'ai_plus_description', standard: 'ai_description' },
    { plus: 'key_insights_plus', standard: 'key_insights' },
    { plus: 'conclusion_plus', standard: 'conclusion' },
  ]

  // Handle arrays by processing each element
  if (Array.isArray(obj)) {
    return obj.map((item) => washReportDocument(item))
  }

  // Process object properties
  const result = { ...obj }

  // Check for property pairs at this level
  for (const { plus, standard } of propertyPairs) {
    if (plus in result && standard in result) {
      // Copy the content from plus version to standard version
      result[standard] = result[plus]
    }
  }

  // Recursively process all properties
  for (const key in result) {
    result[key] = washReportDocument(result[key])
  }

  return result
}

export default washReportDocument
